import { createPortal } from 'react-dom';
import { css } from '@linaria/core';

import type { EditorProps } from '../types';
import { useClickOutside, useLatestFunc } from '../hooks';
import { useState, useEffect } from 'react';

const editorContainer = css`
  display: contents;
`;

const editorContainerClassname = `rdg-editor-container ${editorContainer}`;

export default function EditorContainer<R, SR>({
  row,
  column,
  width,
  onRowChange,
  ...props
}: EditorProps<R, SR>) {
  const [cacheRow, setCacheRow] = useState(row);
  const [commitChanges, setCommitChanges] = useState(false);

  const handleCloseRowChange = useLatestFunc(() => {
    onRowChange({ ...cacheRow } as Readonly<R>, true);
  });

  const onClickCapture = useClickOutside(handleCloseRowChange);

  const handleRowChange = (row: Readonly<R>, commitChanges = false) => {
    setCacheRow(row);
    setCommitChanges(commitChanges);
  };

  useEffect(() => {
    if (commitChanges) {
      onRowChange(cacheRow as Readonly<R>, true);
    }
  }, [commitChanges, cacheRow]);

  useEffect(() => {
    return () => {
      handleCloseRowChange();
    };
  }, []);

  if (column.editor === undefined) {
    return null;
  }
  const editor = (
    <div className={editorContainerClassname} onClickCapture={onClickCapture}>
      <column.editor
        width={width}
        row={cacheRow}
        column={column}
        onRowChange={handleRowChange}
        {...props}
      />
    </div>
  );

  if (column.editorOptions?.createPortal) {
    return createPortal(editor, props.editorPortalTarget);
  }

  return editor;
}
